import { useEffect, useState } from "react";
import { toast } from "sonner";
import { X, Loader2, Save, Sparkles, Lock, Unlock } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import Portal from "./Portal";

const when = (iso) => (iso ? new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }) : "never");

export default function ChannelCommissionsDialog({ onClose }) {
  const [d, setD] = useState(null);
  const [edit, setEdit] = useState({});
  const [busy, setBusy] = useState("");

  const load = () => api.get("/pms/admin/channel-commissions").then(({ data }) => {
    setD(data);
    setEdit(Object.fromEntries(data.rows.filter((r) => r.locked).map((r) => [r.channel, r.rate])));
  }).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);

  const save = async () => {
    setBusy("save");
    try {
      await api.put("/pms/admin/channel-commissions", { overrides: edit });
      toast.success("Channel commissions saved — Revenue Reports now use these rates");
      await load();
    } catch (e) { toast.error(errMsg(e)); }
    setBusy("");
  };
  const refreshAi = async () => {
    setBusy("ai");
    try {
      const { data } = await api.post("/pms/admin/channel-commissions/refresh-ai");
      toast.success(`AI refreshed ${Object.keys(data.rates).length} channel rates`);
      await load();
    } catch (e) { toast.error(errMsg(e)); }
    setBusy("");
  };

  return (
    <Portal>
    <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-[80] grid place-items-center p-4 sm:p-6 overflow-y-auto" onClick={onClose}>
      <div data-testid="channel-commissions-dialog" className="bg-white rounded-2xl w-[96vw] min-h-[92vh] max-h-[92vh] max-w-none shadow-2xl flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="border-b border-slate-100 px-6 py-4 flex items-center gap-4 shrink-0">
          <div className="flex-1">
            <p className="text-[10px] uppercase font-semibold tracking-wider text-slate-400">Cross-property · used by Detail Revenue Report</p>
            <h2 className="font-display text-xl font-bold text-slate-900">Channel Commissions</h2>
          </div>
          <button data-testid="commissions-close" onClick={onClose} className="text-slate-400 hover:text-slate-700"><X size={18} /></button>
        </div>
        {!d ? (
          <div className="p-10 flex justify-center"><Loader2 className="animate-spin text-slate-400" size={22} /></div>
        ) : (
          <div className="p-6 space-y-5 flex-1 overflow-y-auto">
            <div className="bg-indigo-50 border border-indigo-200 rounded-lg px-4 py-3 text-[11px] text-indigo-900 flex items-start gap-3">
              <Sparkles size={16} className="mt-0.5 shrink-0" />
              <div className="flex-1">
                <b>AI keeps these current.</b> On the 1st of every month one low-cost AI call ({d.ai_model || "gpt-5.4-mini"}) re-checks each OTA's host-side commission.
                Rows you edit are <b>locked</b> and never changed by AI. Last AI update: <b data-testid="ai-updated-at">{when(d.ai_updated_at)}</b>.
                {d.ai_last_error && <span className="block text-rose-700 mt-1">Last error: {d.ai_last_error}</span>}
              </div>
              <button data-testid="commissions-refresh-ai" onClick={refreshAi} disabled={!!busy}
                className="shrink-0 rounded-full border border-indigo-300 bg-white px-3 py-1.5 text-[11px] font-semibold text-indigo-700 hover:bg-indigo-100 flex items-center gap-1 disabled:opacity-50">
                {busy === "ai" ? <Loader2 className="animate-spin" size={12} /> : <Sparkles size={12} />} Refresh now
              </button>
            </div>
            <table className="w-full text-sm">
              <thead><tr className="text-[10px] uppercase tracking-wider text-slate-400 border-b border-slate-100">
                <th className="text-left py-2">Channel</th><th className="text-right py-2">Commission %</th><th className="text-left py-2 pl-4">Source</th><th className="text-left py-2">AI suggestion</th><th></th>
              </tr></thead>
              <tbody>
                {d.rows.map((r) => {
                  const locked = r.channel in edit;
                  return (
                    <tr key={r.channel} data-testid={`commission-row-${r.channel.replace(/\W/g, "")}`} className="border-b border-slate-50">
                      <td className="py-2 font-semibold text-slate-800">{r.channel}</td>
                      <td className="py-2 text-right">
                        <input type="number" step="0.1" min="0" max="60" data-testid={`commission-input-${r.channel.replace(/\W/g, "")}`}
                          value={locked ? edit[r.channel] : r.rate}
                          onChange={(e) => setEdit({ ...edit, [r.channel]: e.target.value === "" ? 0 : parseFloat(e.target.value) })}
                          className={`w-24 text-right border rounded-lg px-2 py-1.5 font-mono ${locked ? "border-indigo-400 bg-indigo-50" : "border-slate-300"}`} />
                        <span className="text-xs text-slate-400 ml-1">%</span>
                      </td>
                      <td className="py-2 pl-4 text-xs">
                        <span className={`rounded-full px-2 py-0.5 font-semibold ${locked ? "bg-indigo-100 text-indigo-700" : r.source === "ai" ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-600"}`}>
                          {locked ? "Manual · locked" : r.source === "ai" ? "AI" : "Default"}
                        </span>
                      </td>
                      <td className="py-2 text-xs text-slate-500">{r.ai_rate != null ? `${r.ai_rate}% · ${r.ai_note}` : "—"}</td>
                      <td className="py-2 text-right">
                        {locked ? (
                          <button title="Unlock — let AI manage this row" onClick={() => { const n = { ...edit }; delete n[r.channel]; setEdit(n); }} className="text-indigo-600 hover:text-indigo-800"><Lock size={14} /></button>
                        ) : (
                          <button title="Lock at current value" onClick={() => setEdit({ ...edit, [r.channel]: r.rate })} className="text-slate-300 hover:text-slate-600"><Unlock size={14} /></button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <p className="text-[11px] text-slate-500">Revenue Report: Commission = travel-agent commission if one is logged for the booking, otherwise Total × channel %. Atiti and Direct stay at 0% unless you set otherwise.</p>
            <div className="flex justify-end gap-2 pt-3 border-t border-slate-100">
              <button onClick={onClose} className="text-sm text-slate-500 hover:text-slate-800 px-4 py-2">Cancel</button>
              <button data-testid="commissions-save" onClick={save} disabled={!!busy}
                className="flex items-center gap-2 bg-[#4F46E5] hover:bg-[#4338CA] text-white text-sm font-semibold rounded-full px-5 py-2 disabled:opacity-50">
                {busy === "save" ? <Loader2 className="animate-spin" size={13} /> : <Save size={13} />} Save
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
    </Portal>
  );
}
